import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, CheckCircle2, GraduationCap, Info, Lock, PartyPopper, User, X } from 'lucide-react';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import PageHero from '../components/common/PageHero';
import { toast } from 'react-toastify';
import api from '../services/api';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

const AlumniRegistration = () => {
  const [departments, setDepartments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [examBodies, setExamBodies] = useState([]);
  const [graduationDays, setGraduationDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [courseSearchTerm, setCourseSearchTerm] = useState('');
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [registrationData, setRegistrationData] = useState(null);
  const [countdown, setCountdown] = useState(50);
  const [copiedField, setCopiedField] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [profilePreview, setProfilePreview] = useState(null);

  const [formData, setFormData] = useState({
    firstName: '',
    secondName: '',
    email: '',
    admissionNumber: '',
    mobileNumber: '',
    departmentId: '',
    courseId: '',
    examBodyId: '',
    grade: '',
    graduationDayId: ''
  });

  // Countdown timer effect
  useEffect(() => {
    let timer;
    if (showSuccessModal && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (countdown === 0) {
      setShowSuccessModal(false);
      setCountdown(50);
      setCopiedField(null);
    }
    return () => clearTimeout(timer);
  }, [showSuccessModal, countdown]);

  const getGradeOptions = (examBodyId) => {
    const examBody = examBodies.find(eb => eb.id === parseInt(examBodyId));
    if (!examBody) return [];

    switch(examBody.code) {
      case 'TVET-CDACC':
        return [
          { value: 'Mastery', label: 'Mastery' },
          { value: 'Proficiency', label: 'Proficiency' },
          { value: 'Competent', label: 'Competent' }
        ];
      case 'KNEC':
        return [
          { value: 'Distinction', label: 'Distinction' },
          { value: 'Credit', label: 'Credit' },
          { value: 'Pass', label: 'Pass' }
        ];
      case 'NITA':
        return [
          { value: 'First Class', label: 'First Class' },
          { value: 'Pass', label: 'Pass' }
        ];
      default:
        return [
          { value: 'Distinction', label: 'Distinction' },
          { value: 'Credit', label: 'Credit' },
          { value: 'Pass', label: 'Pass' }
        ];
    }
  };

  const getCourseDisplayName = (course) => {
    let displayName = course.name;
    if (course.exam_body_code === 'TVET-CDACC' && course.level_number) {
      displayName = displayName + ' (Level ' + course.level_number + ')';
    } else if (course.level_name && course.level_name.trim() !== '') {
      displayName = displayName + ' (' + course.level_name + ')';
    }
    return displayName;
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (formData.departmentId) {
      const filtered = courses.filter(course => course.department_id === parseInt(formData.departmentId));
      setFilteredCourses(filtered);
      setFormData(prev => ({ ...prev, courseId: '' }));
      setCourseSearchTerm('');
    } else {
      setFilteredCourses([]);
    }
  }, [formData.departmentId, courses]);

  useEffect(() => {
    if (courseSearchTerm && formData.departmentId) {
      const filtered = courses.filter(course =>
        course.department_id === parseInt(formData.departmentId) &&
        course.name.toLowerCase().includes(courseSearchTerm.toLowerCase())
      );
      setFilteredCourses(filtered);
      setShowCourseDropdown(true);
    } else if (formData.departmentId) {
      const filtered = courses.filter(course => course.department_id === parseInt(formData.departmentId));
      setFilteredCourses(filtered);
      setShowCourseDropdown(false);
    }
  }, [courseSearchTerm, courses, formData.departmentId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [deptsRes, coursesRes, examRes, graduationDaysRes] = await Promise.all([
        api.get('/departments'),
        api.get('/courses'),
        api.get('/exam-bodies'),
        api.get('/graduation-days/past')
      ]);

      setDepartments(deptsRes.data);
      setCourses(coursesRes.data);
      setExamBodies(examRes.data);
      setGraduationDays(graduationDaysRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load registration data');
    } finally {
      setLoading(false);
    }
  };

  const handleAdmissionNumberChange = (e) => {
    let value = e.target.value.toUpperCase();
    setFormData({ ...formData, admissionNumber: value });
  };

  const handleMobileNumberChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.startsWith('0') && value.length <= 10) {
      value = value.substring(0, 10);
    } else if (value.startsWith('254') && value.length <= 12) {
      value = value.substring(0, 12);
    } else if (value.length <= 9) {
      value = value;
    }
    setFormData({ ...formData, mobileNumber: value });
  };

  const handleCourseSelect = (course) => {
    setFormData({ ...formData, courseId: course.id });
    setCourseSearchTerm(getCourseDisplayName(course));
    setShowCourseDropdown(false);
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success(field + ' copied to clipboard!');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Profile photo must be smaller than 5MB');
      return;
    }

    setProfileImage(file);
    setProfilePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {    e.preventDefault();

    if (!formData.firstName || !formData.secondName || !formData.email ||
        !formData.admissionNumber || !formData.mobileNumber || !formData.departmentId ||
        !formData.courseId || !formData.examBodyId || !formData.grade || !formData.graduationDayId) {
      toast.error('Please fill in all required fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    const phoneRegex = /^(07\d{8}|2547\d{8})$/;
    if (!phoneRegex.test(formData.mobileNumber)) {
      toast.error('Please enter a valid Kenyan phone number');
      return;
    }

    const admissionRegex = /^[A-Z]{3}\/\d{3}\/[A-Z]\d{4}$/;
    if (!admissionRegex.test(formData.admissionNumber)) {
      toast.error('Invalid admission number format. Use format: DEE/001/S2021');
      return;
    }

    setSubmitting(true);
    try {
      const alumniData = {
        name: formData.firstName + ' ' + formData.secondName,
        email: formData.email,
        admin_number: formData.admissionNumber,
        telephone: formData.mobileNumber,
        department_id: parseInt(formData.departmentId),
        course_id: parseInt(formData.courseId),
        exam_body_id: parseInt(formData.examBodyId),
        grade: formData.grade,
        graduation_day_id: parseInt(formData.graduationDayId)
      };

      // Multipart so the optional profile photo travels with the form.
      const payload = new FormData();
      Object.entries(alumniData).forEach(([key, value]) => payload.append(key, value));
      if (profileImage) {
        payload.append('profile_image', profileImage);
      }

      const response = await api.post('/alumni/register', payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const selectedDept = departments.find(d => d.id === parseInt(formData.departmentId));
      const selectedCourse = courses.find(c => c.id === parseInt(formData.courseId));
      const selectedGradDay = graduationDays.find(g => g.id === parseInt(formData.graduationDayId));

      setRegistrationData({
        name: formData.firstName + ' ' + formData.secondName,
        email: formData.email,
        admin_number: formData.admissionNumber,
        telephone: formData.mobileNumber,
        membership_number: response.data.alumni_membership_number || 'Pending...',
        department: selectedDept?.name || 'N/A',
        course: selectedCourse ? getCourseDisplayName(selectedCourse) : 'N/A',
        grade: formData.grade,
        graduation_year: selectedGradDay?.year || 'N/A',
        username: formData.mobileNumber,
        password: formData.admissionNumber
      });

      setShowSuccessModal(true);
      setCountdown(50);

      // Reset form
      setFormData({
        firstName: '',
        secondName: '',
        email: '',
        admissionNumber: '',
        mobileNumber: '',
        departmentId: '',
        courseId: '',
        examBodyId: '',
        grade: '',
        graduationDayId: ''
      });
      setCourseSearchTerm('');
      setShowCourseDropdown(false);
      setProfileImage(null);
      setProfilePreview(null);

    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const currentGradeOptions = getGradeOptions(formData.examBodyId);
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-KE', options);
  };

  return (
    <>
      <Header />
      <div className="min-h-[calc(100vh-8rem)] bg-ink-50">
        <PageHero
          icon={GraduationCap}
          title="Alumni Registration"
          subtitle="Join our alumni community and stay connected with Katine TTI"
        />

        <div className="container-page py-8 sm:py-12">
          <div className="mx-auto max-w-4xl">
            <Card className="p-4 sm:p-6 md:p-8">
              <form onSubmit={handleSubmit}>
                {/* Profile photo */}
                <div className="mb-6 flex flex-col items-start gap-4 rounded-xl border border-ink-200 bg-ink-50 p-4 sm:flex-row sm:items-center">
                  <div className="relative flex h-20 w-20 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-brand-800 text-xl font-bold text-white/80">
                    {profilePreview ? (
                      <img
                        src={profilePreview}
                        alt="Selected profile photo"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span aria-hidden="true" className="text-xl">
                        {(formData.firstName?.charAt(0) || '') + (formData.secondName?.charAt(0) || '') || (
                          <User className="h-8 w-8 text-white/70" />
                        )}
                      </span>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <label htmlFor="profile-photo" className="label mb-1">
                      Profile photo <span className="font-normal text-ink-500">(optional)</span>
                    </label>
                    <p className="mb-2 text-xs text-ink-500">
                      Shown on your dashboard and in the alumni community. JPG, PNG or WebP, up to 5MB.
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        id="profile-photo"
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        onChange={handlePhotoChange}
                        className="block w-full max-w-xs cursor-pointer rounded-lg border border-ink-300 bg-white text-sm text-ink-600 file:mr-3 file:cursor-pointer file:rounded-l-lg file:border-0 file:bg-brand-50 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-brand-700 hover:file:bg-brand-100"
                      />
                      {profilePreview && (
                        <button
                          type="button"
                          onClick={() => {
                            setProfileImage(null);
                            setProfilePreview(null);
                          }}
                          className="btn-ghost min-h-0 px-3 py-1.5 text-xs"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2">
                  <Input label="First Name *" value={formData.firstName} onChange={(e) => setFormData({...formData, firstName: e.target.value})} placeholder="Enter your first name" required />
                  <Input label="Second Name *" value={formData.secondName} onChange={(e) => setFormData({...formData, secondName: e.target.value})} placeholder="Enter your second name" required />
                  <Input label="Email Address *" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="your.email@example.com" required />

                  <div>
                    <label className="label">Admission Number *</label>
                    <Input type="text" value={formData.admissionNumber} onChange={handleAdmissionNumberChange} placeholder="DEE/001/S2021" className="uppercase" required />
                    <p className="mt-1 text-xs text-ink-500">Format: DEE/001/S2021 (auto-capitalized)</p>
                  </div>

                  <div>
                    <label className="label">Mobile Number *</label>
                    <Input type="tel" value={formData.mobileNumber} onChange={handleMobileNumberChange} placeholder="0712345678 or 254712345678" required />
                    <p className="mt-1 text-xs text-ink-500">Kenyan format (e.g., 0712345678)</p>
                  </div>

                  <div>
                    <label className="label">Department *</label>
                    <select className="input-field" value={formData.departmentId} onChange={(e) => setFormData({...formData, departmentId: e.target.value, courseId: '', courseSearchTerm: ''})} required>
                      <option value="">-- Select Department --</option>
                      {departments.map(dept => (<option key={dept.id} value={dept.id}>{dept.name}</option>))}
                    </select>
                  </div>

                  <div className="relative">
                    <label className="label">Course *</label>
                    <input type="text" className="input-field disabled:cursor-not-allowed disabled:bg-ink-100" placeholder={formData.departmentId ? "Start typing to search course..." : "Select department first"} value={courseSearchTerm} onChange={(e) => setCourseSearchTerm(e.target.value)} onFocus={() => formData.departmentId && setShowCourseDropdown(true)} onBlur={() => setTimeout(() => setShowCourseDropdown(false), 200)} disabled={!formData.departmentId} />
                    {showCourseDropdown && filteredCourses.length > 0 && (
                      <div className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-ink-200 bg-white shadow-card">
                        {filteredCourses.map(course => (
                          <div key={course.id} onClick={() => handleCourseSelect(course)} className="cursor-pointer border-b border-ink-100 px-4 py-2 last:border-b-0 hover:bg-ink-50">
                            <div className="font-medium text-ink-900">{getCourseDisplayName(course)}</div>
                            {course.level_name && course.level_name.trim() !== '' && (
                              <div className="text-xs text-ink-500">{course.level_name}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {!formData.departmentId && <p className="mt-1 text-xs text-ink-500">Please select a department first</p>}
                  </div>

                  <div>
                    <label className="label">Exam Body *</label>
                    <select className="input-field" value={formData.examBodyId} onChange={(e) => setFormData({...formData, examBodyId: e.target.value, grade: ''})} required>
                      <option value="">-- Select Exam Body --</option>
                      {examBodies.map(body => (<option key={body.id} value={body.id}>{body.name}</option>))}
                    </select>
                  </div>

                  <div>
                    <label className="label">Grade *</label>
                    <select className="input-field" value={formData.grade} onChange={(e) => setFormData({...formData, grade: e.target.value})} required disabled={!formData.examBodyId}>
                      <option value="">-- Select Grade --</option>
                      {currentGradeOptions.map(grade => (<option key={grade.value} value={grade.value}>{grade.label}</option>))}
                    </select>
                    {!formData.examBodyId && <p className="mt-1 text-xs text-ink-500">Please select an exam body first</p>}
                  </div>

                  <div>
                    <label className="label">Graduation Ceremony *</label>
                    <select className="input-field" value={formData.graduationDayId} onChange={(e) => setFormData({...formData, graduationDayId: e.target.value})} required disabled={graduationDays.length === 0}>
                      <option value="">-- Select Graduation Ceremony --</option>
                      {graduationDays.map(day => (<option key={day.id} value={day.id}>{formatDate(day.ceremony_date)} - {day.year}{day.venue && ` @ ${day.venue}`}</option>))}
                    </select>
                    {graduationDays.length === 0 && <p className="mt-1 text-xs text-gold-700">No past graduation ceremonies available.</p>}
                  </div>
                </div>

                <div className="mt-6 rounded-xl border border-brand-100 bg-brand-50 p-3 sm:p-4">
                  <p className="text-xs text-brand-800 sm:text-sm">
                    <strong className="inline-flex items-center gap-1.5">
                      <Info className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                      Login Information:
                    </strong><br />
                    Your username will be your mobile number and your password will be your admission number.<br />
                    You will be able to log in after admin approval.
                  </p>
                </div>

                <div className="mt-6">
                  <Button type="submit" className="w-full py-3 text-base sm:py-4 sm:text-lg" disabled={submitting || graduationDays.length === 0}>
                    {submitting ? 'Registering...' : 'Register as Alumnus'}
                  </Button>
                </div>
              </form>
            </Card>

            <div className="mt-6 text-center">
              <p className="text-sm text-ink-500 sm:text-base">
                Already registered? <Link to="/alumni/login" className="font-semibold text-brand-700 hover:underline">Login to Alumni Portal</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />

      {/* Success Modal */}
      {showSuccessModal && registrationData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-950/60 p-4">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-ink-200 bg-white shadow-card">
            <div className="sticky top-0 flex items-center justify-between bg-gradient-to-r from-emerald-600 to-emerald-500 px-6 py-4 text-white">
              <div className="flex items-center gap-2">
                <PartyPopper className="h-6 w-6 flex-shrink-0" aria-hidden="true" />
                <h2 className="text-xl font-bold">Registration Successful!</h2>
              </div>
              <button
                onClick={() => setShowSuccessModal(false)}
                aria-label="Close"
                className="text-white transition-colors hover:text-white/80"
              >
                <X className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="mb-6 text-center">
                <div className="mx-auto mb-3 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-100">
                  <CheckCircle2 className="h-10 w-10 text-emerald-600" aria-hidden="true" />
                </div>
                <p className="text-ink-500">Your alumni registration has been completed successfully.</p>
                <p className="mt-1 text-sm text-ink-500">A confirmation email has been sent to your email address.</p>
                <p className="text-sm text-ink-500">This modal will auto-close in <strong>{countdown}</strong> seconds</p>
              </div>
              
              {/* Membership Details */}
              <div className="mb-4 rounded-xl bg-gradient-to-r from-brand-50 to-brand-100 p-4">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h3 className="inline-flex items-center gap-1.5 font-bold text-brand-700">
                    <GraduationCap className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                    Alumni Membership Details
                  </h3>
                  <button 
                    onClick={() => copyToClipboard(registrationData.membership_number, 'Membership Number')}
                    className="rounded bg-brand-700 px-2 py-1 text-xs text-white transition-colors hover:bg-brand-800"
                  >
                    {copiedField === 'Membership Number' ? (
                      <span className="inline-flex items-center gap-1">
                        <Check className="h-3 w-3" aria-hidden="true" />
                        Copied!
                      </span>
                    ) : 'Copy'}
                  </button>
                </div>
                <p className="text-center text-2xl font-bold text-brand-700">{registrationData.membership_number}</p>
              </div>
              
              {/* Personal Details Grid */}
              <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="rounded-xl bg-ink-50 p-3">
                  <label className="text-xs text-ink-500">Full Name</label>
                  <p className="font-medium text-ink-900">{registrationData.name}</p>
                </div>
                <div className="rounded-xl bg-ink-50 p-3">
                  <label className="text-xs text-ink-500">Admission Number</label>
                  <p className="font-mono text-sm text-ink-900">{registrationData.admin_number}</p>
                </div>
                <div className="rounded-xl bg-ink-50 p-3">
                  <label className="text-xs text-ink-500">Email Address</label>
                  <p className="break-all text-sm text-ink-900">{registrationData.email}</p>
                </div>
                <div className="rounded-xl bg-ink-50 p-3">
                  <label className="text-xs text-ink-500">Phone Number</label>
                  <p className="text-ink-900">{registrationData.telephone}</p>
                </div>
                <div className="rounded-xl bg-ink-50 p-3">
                  <label className="text-xs text-ink-500">Department</label>
                  <p className="text-sm text-ink-900">{registrationData.department}</p>
                </div>
                <div className="rounded-xl bg-ink-50 p-3">
                  <label className="text-xs text-ink-500">Course</label>
                  <p className="text-sm text-ink-900">{registrationData.course}</p>
                </div>
                <div className="rounded-xl bg-ink-50 p-3">
                  <label className="text-xs text-ink-500">Grade</label>
                  <p className="text-ink-900">{registrationData.grade}</p>
                </div>
                <div className="rounded-xl bg-ink-50 p-3">
                  <label className="text-xs text-ink-500">Graduation Year</label>
                  <p className="text-ink-900">{registrationData.graduation_year}</p>
                </div>
              </div>
              
              {/* Login Credentials */}
              <div className="mb-4 rounded-xl border border-gold-200 bg-gold-50 p-4">
                <h3 className="mb-2 inline-flex items-center gap-1.5 font-bold text-gold-900">
                  <Lock className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                  Login Credentials
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-ink-600">Username:</span>
                    <code className="rounded bg-white px-2 py-1 text-sm">{registrationData.username}</code>
                    <button 
                      onClick={() => copyToClipboard(registrationData.username, 'Username')}
                      className="inline-flex items-center gap-1 text-xs text-brand-700 hover:underline"
                    >
                      {copiedField === 'Username' ? (
                        <Check className="h-3 w-3" aria-hidden="true" />
                      ) : 'Copy'}
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-ink-600">Password:</span>
                    <code className="rounded bg-white px-2 py-1 text-sm">{registrationData.password}</code>
                    <button 
                      onClick={() => copyToClipboard(registrationData.password, 'Password')}
                      className="inline-flex items-center gap-1 text-xs text-brand-700 hover:underline"
                    >
                      {copiedField === 'Password' ? (
                        <Check className="h-3 w-3" aria-hidden="true" />
                      ) : 'Copy'}
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-3">
                <button 
                  onClick={() => { setShowSuccessModal(false); window.location.href = '/alumni/login'; }}
                  className="btn-primary flex-1"
                >
                  Proceed to Login
                </button>
                <button 
                  onClick={() => setShowSuccessModal(false)}
                  className="btn-outline flex-1"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AlumniRegistration;
